import React, { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import { Download, X } from "lucide-react";
import { Student } from "@/services/db/types";

interface StudentCertificatesPageSelectionDialogProps {
  isOpen: boolean;
  onClose: () => void;
  student: Student;
  onDownload: (selectedPrograms: string[]) => void;
}

const StudentCertificatesPageSelectionDialog = ({ 
  isOpen, 
  onClose, 
  student,
  onDownload
}: StudentCertificatesPageSelectionDialogProps) => {
  const [selectedPrograms, setSelectedPrograms] = useState<string[]>([]);
  
  const programs = student.programs || [];

  const handleProgramToggle = (programTitle: string, checked: boolean) => {
    if (checked) {
      setSelectedPrograms([...selectedPrograms, programTitle]);
    } else {
      setSelectedPrograms(selectedPrograms.filter(p => p !== programTitle));
    }
  };

  const handleSelectAll = () => {
    if (selectedPrograms.length === programs.length) {
      setSelectedPrograms([]);
    } else {
      setSelectedPrograms(programs.map(p => p.title));
    }
  };

  const handleDownload = () => {
    if (selectedPrograms.length === 0) return;
    
    console.log('Certificates selection:', { selectedPrograms });
    onDownload(selectedPrograms);
    onClose();
  };

  const handleOpenChange = (open: boolean) => {
    if (!open) {
      onClose();
      setSelectedPrograms([]);
    }
  };

  const fullName = student.surname ? `${student.name} ${student.surname}` : student.name;

  return (
    <Dialog open={isOpen} onOpenChange={handleOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center justify-between">
            Select Certificates - {fullName}
            <Button
              variant="ghost"
              size="sm"
              onClick={onClose}
              className="h-6 w-6 p-0"
            >
              <X className="h-4 w-4" />
            </Button>
          </DialogTitle>
          <DialogDescription>
            Choose which program certificates to download ({programs.length} programs available)
          </DialogDescription>
        </DialogHeader>
        
        <div className="space-y-4">
          <div className="flex items-center space-x-2">
            <Checkbox
              id="selectAll"
              checked={selectedPrograms.length === programs.length && programs.length > 0}
              onCheckedChange={handleSelectAll}
            />
            <Label htmlFor="selectAll" className="text-sm font-medium">
              Select All Programs
            </Label>
          </div>

          <div className="max-h-60 overflow-y-auto space-y-2">
            {programs.map((program, index) => (
              <div key={index} className="flex items-center space-x-2 p-2 border rounded">
                <Checkbox
                  id={`program-${index}`}
                  checked={selectedPrograms.includes(program.title)}
                  onCheckedChange={(checked) => handleProgramToggle(program.title, checked as boolean)}
                />
                <div className="flex-1">
                  <Label htmlFor={`program-${index}`} className="text-sm font-medium">
                    {program.title}
                  </Label>
                  <div className="text-xs text-muted-foreground">
                    {program.category} • {program.feeStatus || 'pending'}
                  </div>
                </div>
              </div>
            ))}
          </div>
          
          <div className="text-sm text-muted-foreground">
            Selected: {selectedPrograms.length} of {programs.length} programs
          </div>

          <div className="flex justify-end space-x-2 pt-4 border-t">
            <Button variant="outline" onClick={onClose}>
              Cancel
            </Button>
            <Button onClick={handleDownload} disabled={selectedPrograms.length === 0}>
              <Download className="w-4 h-4 mr-2" />
              Continue ({selectedPrograms.length} certificates)
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default StudentCertificatesPageSelectionDialog;
